/**
 * The Materials panel (ADR-020 T2, ratified §11: EXPORT-REAL) — per-material
 * glTF factor controls. Factors multiply the textures (glTF semantics), so
 * the wet-skin cure on the satin-baked class is one roughness slider; unlike
 * the Character panel's tenants these edits are the character's TRUTH and
 * ride into every export — the derive step bakes a >1 roughness into the
 * texture so the shipped file stays spec-clean.
 *
 * Six channels, two shapes:
 *  - SCALARS as value rows — metallic [0,1], roughness [0,2], normal scale
 *    [0,2], occlusion strength [0,1]. Bands are PER MATERIAL, asked at
 *    render time: roughness reaches 2 only where a roughness texture exists
 *    to bake into (a textureless >1 would refuse at export — the band keeps
 *    the unreachable value unrepresentable, the §12 posture); normal scale
 *    and occlusion strength are texture ATTRIBUTES, so without their map the
 *    row renders its refusal instead of a dead slider.
 *  - COLORS as color rows — base-color tint and emissive. The picker speaks
 *    sRGB hex (what a person sees); the Document stores LINEAR (glTF's own
 *    space); this file owns that conversion at the edge.
 *
 * The material LIST is per subject and rebuilds when the subject swaps —
 * rows are keyed by name+capability signature, re-rendered on every notify
 * like every panel (undo/redo and a loaded Document move the knobs).
 *
 * Depends on: panel-registry (types), value-row, tooltip, kernel (band
 * constants). Depended on by: main (registration site).
 */
import { METALLIC_FACTOR_MAX, NORMAL_SCALE_MAX, ROUGHNESS_FACTOR_MAX } from '@harryhausen/kernel';
import type { PanelMount } from './panel-registry.js';
import { createValueRow, type ValueRowHandle } from './value-row.js';
import { attachTip, describeButton } from './tooltip.js';

export type MaterialChannel = 'metallic' | 'roughness' | 'normalScale' | 'occlusionStrength';
export type MaterialColorChannel = 'baseColor' | 'emissive';

/** Linear-space RGB triple — the Document's (and glTF's) color shape. */
export type LinearRgb = readonly [number, number, number];

export interface MaterialChannelUi {
  /** What the display applies right now: explicit edit ?? shipped. */
  readonly value: number;
  /** The file's own factor — the provenance chip's baseline. */
  readonly shipped: number;
  /** True once a material.setFactor committed for this channel. */
  readonly explicit: boolean;
}

export interface MaterialColorUi {
  readonly value: LinearRgb;
  readonly shipped: LinearRgb;
  readonly explicit: boolean;
}

export interface MaterialUi {
  readonly name: string;
  readonly metallic: MaterialChannelUi;
  readonly roughness: MaterialChannelUi;
  readonly normalScale: MaterialChannelUi;
  readonly occlusionStrength: MaterialChannelUi;
  readonly baseColor: MaterialColorUi;
  readonly emissive: MaterialColorUi;
  /** Band ceiling for roughness: reaches matte for THIS material's own map. */
  readonly roughnessMax: number;
  /**
   * Whether each channel currently reads the metallicRoughness map, and
   * whether there is a map to read. Detaching is the cure the FACTOR cannot
   * give when the map itself is wrong.
   */
  readonly metallicFromMap: boolean;
  readonly roughnessFromMap: boolean;
  readonly hasMetallicRoughnessMap: boolean;
  /** The map's own median on each channel — what detaching would stop obeying. */
  readonly measuredRoughness: number | null;
  /** Without the map, normal scale / occlusion strength rows refuse. */
  readonly hasNormalTexture: boolean;
  readonly hasOcclusionTexture: boolean;
}

export type MaterialsUiState =
  | { kind: 'ready'; materials: readonly MaterialUi[] }
  | { kind: 'refused'; reason: string };

export interface MaterialsPanelDeps {
  /** Asked at render time — the subject is replaceable (ADR-017 posture). */
  readonly materials: () => MaterialsUiState;
  /** Commit through the command log — the panel never writes the scene. */
  readonly setFactor: (material: string, channel: MaterialChannel, value: number) => void;
  readonly previewFactor: (material: string, channel: MaterialChannel, value: number | null) => void;
  readonly setColor: (material: string, channel: MaterialColorChannel, value: LinearRgb) => void;
  readonly previewColor: (material: string, channel: MaterialColorChannel, value: LinearRgb | null) => void;
  /** Detach/reattach one channel of the metallicRoughness map. */
  readonly setMapUse: (material: string, channel: 'metallic' | 'roughness', fromMap: boolean) => void;
}

// ---- the sRGB edge: picker hex ⇄ the Document's linear triples ----

const srgbToLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const linearToSrgb = (c: number): number => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);

export function hexToLinear(hex: string): LinearRgb {
  const n = parseInt(hex.replace('#', ''), 16);
  return [srgbToLinear(((n >> 16) & 0xff) / 255), srgbToLinear(((n >> 8) & 0xff) / 255), srgbToLinear((n & 0xff) / 255)];
}

export function linearToHex(rgb: LinearRgb): string {
  const byte = (c: number): string =>
    Math.round(linearToSrgb(Math.min(1, Math.max(0, c))) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${byte(rgb[0])}${byte(rgb[1])}${byte(rgb[2])}`;
}

interface ColorRowSpec {
  readonly label: string;
  readonly tip: string;
  readonly describe: () => string;
  readonly state: () => { value: LinearRgb; explicit: boolean } | null;
  readonly commit: (value: LinearRgb) => void;
  readonly preview: (value: LinearRgb | null) => void;
}

interface ColorRowHandle {
  readonly root: HTMLDivElement;
  render(): void;
}

/**
 * The color twin of the value row: swatch input + provenance chip + the two
 * tooltip tiers. The picker previews live on `input` and commits ONCE on
 * `change` (the gesture contract the sliders keep) — both doors sRGB at the
 * surface, linear underneath.
 */
function createColorRow(spec: ColorRowSpec): ColorRowHandle {
  const root = document.createElement('div');
  root.className = 'value-row color-row';
  root.setAttribute('role', 'group');

  const label = document.createElement('span');
  label.className = 'vr-label';
  label.textContent = spec.label;

  // The color row sits on the value row's EXACT column grid: a spacer holds
  // the slider's flex column, the swatch takes the 56px field column, and a
  // ghost unit keeps the chip/? columns aligned with the rows above — six
  // rows, one grid, no drift (SHELL_CHROME_SPEC §12 density).
  const spacer = document.createElement('span');
  spacer.className = 'vr-color-spacer';

  const input = document.createElement('input');
  input.type = 'color';
  input.className = 'vr-color';
  input.setAttribute('aria-label', `${spec.label} color`);

  const unitGhost = document.createElement('span');
  unitGhost.className = 'vr-unit vr-unit-ghost';
  unitGhost.textContent = '×';
  unitGhost.setAttribute('aria-hidden', 'true');

  const chip = document.createElement('span');
  chip.className = 'vr-chip';
  chip.hidden = true;

  const describe = describeButton(spec.label, spec.describe);
  root.append(label, spacer, input, unitGhost, chip, describe);
  root.appendChild(attachTip(input, spec.tip));

  input.addEventListener('input', () => {
    spec.preview(hexToLinear(input.value));
  });
  input.addEventListener('change', () => {
    spec.preview(null);
    spec.commit(hexToLinear(input.value));
  });

  const render = (): void => {
    const state = spec.state();
    if (state === null) return;
    // Never yank the swatch mid-pick — while the picker has it, the person
    // is the author of its contents (the value-row law, worn here).
    if (document.activeElement !== input) input.value = linearToHex(state.value);
    chip.hidden = false;
    chip.textContent = state.explicit ? 'set' : 'shipped';
  };
  return { root, render };
}

export function createMaterialsPanel(deps: MaterialsPanelDeps): PanelMount {
  return (container, store) => {
    const section = document.createElement('section');
    section.className = 'materials-panel';
    container.append(section);

    const refusal = document.createElement('p');
    refusal.className = 'value-refusal';
    refusal.hidden = true;

    interface Renderable {
      render(): void;
    }
    interface RowSet {
      readonly signature: string;
      readonly rows: readonly Renderable[];
    }
    let built: RowSet | null = null;

    const signatureOf = (materials: readonly MaterialUi[]): string =>
      materials
        // The map-use switches join the signature, or undo/redo moves the
        // Document and the viewport while the checkbox keeps its old tick —
        // the control would then lie about what the surface is doing
        // (Codex P2, #115).
        .map(
          (m) =>
            `${m.name}:${m.roughnessMax}:${m.hasNormalTexture}:${m.hasOcclusionTexture}:` +
            `${m.metallicFromMap}:${m.roughnessFromMap}:${m.hasMetallicRoughnessMap}`,
        )
        .join('|');

    const buildRows = (materials: readonly MaterialUi[]): RowSet => {
      section.replaceChildren();
      const rows: Renderable[] = [];
      for (const material of materials) {
        if (materials.length > 1) {
          const heading = document.createElement('h4');
          heading.className = 'materials-panel-name';
          heading.textContent = material.name;
          section.append(heading);
        }
        const find = (): MaterialUi | undefined => {
          const state = deps.materials();
          return state.kind === 'refused' ? undefined : state.materials.find((x) => x.name === material.name);
        };
        const scalar = (
          label: string,
          which: MaterialChannel,
          max: number,
          tip: string,
          explain: string,
          /** null = the channel is live; a string = the refusal reason. */
          refusedReason: string | null,
        ): ValueRowHandle => {
          const channelOf = (m: MaterialUi): MaterialChannelUi =>
            which === 'metallic'
              ? m.metallic
              : which === 'roughness'
                ? m.roughness
                : which === 'normalScale'
                  ? m.normalScale
                  : m.occlusionStrength;
          const row = createValueRow({
            label,
            unit: '×',
            min: 0,
            max,
            step: 0.01,
            precision: 2,
            tip,
            // Read at open time so the shipped value in the explainer is live.
            describe: () => {
              const m = find();
              if (m === undefined) return `Material "${material.name}" is not on this subject.`;
              if (refusedReason !== null) return `Unavailable here: ${refusedReason}.`;
              return (
                `${explain} The file shipped **${channelOf(m).shipped.toFixed(2)}**. ` +
                `Export-real (ADR-020 §11): this value rides into every exported file` +
                (which === 'roughness' && m.roughnessMax > 1
                  ? ' — above 1.0 it is baked into the roughness texture so the file stays spec-clean.'
                  : '.')
              );
            },
            state: () => {
              const m = find();
              if (m === undefined) return { kind: 'refused', reason: `material "${material.name}" left the stage` };
              if (refusedReason !== null) return { kind: 'refused', reason: refusedReason };
              const ch = channelOf(m);
              return { kind: 'ready', value: ch.value, chip: ch.explicit ? 'set' : 'shipped' };
            },
            commit: (value) => {
              deps.setFactor(material.name, which, value);
            },
            preview: (value) => {
              deps.previewFactor(material.name, which, value);
            },
          });
          section.append(row.root, row.refusal);
          return row;
        };
        const color = (
          label: string,
          which: MaterialColorChannel,
          tip: string,
          explain: string,
        ): ColorRowHandle => {
          const row = createColorRow({
            label,
            tip,
            describe: () => {
              const m = find();
              if (m === undefined) return `Material "${material.name}" is not on this subject.`;
              const ch = which === 'baseColor' ? m.baseColor : m.emissive;
              return (
                `${explain} The file shipped **${linearToHex(ch.shipped)}**. ` +
                `Export-real (ADR-020 §11): this color rides into every exported file.`
              );
            },
            state: () => {
              const m = find();
              if (m === undefined) return null;
              const ch = which === 'baseColor' ? m.baseColor : m.emissive;
              return { value: ch.value, explicit: ch.explicit };
            },
            commit: (value) => {
              deps.setColor(material.name, which, value);
            },
            preview: (value) => {
              deps.previewColor(material.name, which, value);
            },
          });
          section.append(row.root);
          return row;
        };
        /**
         * The "from map" switch, one per channel — World's Materials panel
         * offers exactly this and it is what actually cured these characters
         * there, so the suite says one thing in two places.
         *
         * It sits ABOVE the sliders because it changes what they mean: with
         * the map attached the factor multiplies it, detached the factor IS
         * the value.
         */
        const fromMap = (label: string, which: 'metallic' | 'roughness', on: boolean): void => {
          if (!material.hasMetallicRoughnessMap) return;
          const row = document.createElement('label');
          row.className = 'material-frommap';
          const box = document.createElement('input');
          box.type = 'checkbox';
          box.checked = on;
          box.addEventListener('change', (event) => {
            if (!event.isTrusted) return;
            deps.setMapUse(material.name, which, box.checked);
          });
          const text = document.createElement('span');
          const measured =
            which === 'roughness' && material.measuredRoughness !== null
              ? ` — the map says ≈ ${material.measuredRoughness.toFixed(2)}`
              : '';
          text.textContent = `${label} from map${measured}`;
          row.append(box, text);
          section.append(row);
        };
        fromMap('Metallic', 'metallic', material.metallicFromMap);
        fromMap('Roughness', 'roughness', material.roughnessFromMap);
        rows.push(
          scalar(
            'Metallic',
            'metallic',
            METALLIC_FACTOR_MAX,
            'Multiplies the metallic channel',
            'glTF metallicFactor — multiplies the metallic texture (or stands alone without one).',
            null,
          ),
          scalar(
            'Roughness',
            'roughness',
            Math.min(material.roughnessMax, ROUGHNESS_FACTOR_MAX),
            'Multiplies the roughness texture — raise to cure wet-look skin',
            'glTF roughnessFactor — multiplies the roughness texture. Tripo bakes satin ~0.53 over skin; ~1.2–1.4 here reads as dry skin.',
            null,
          ),
          scalar(
            'Normal',
            'normalScale',
            NORMAL_SCALE_MAX,
            'Scales the normal map — 0 flattens, 2 doubles',
            'glTF normalTexture.scale — bump strength. Tripo bakes can arrive too strong or too faint; this is the one-knob correction.',
            material.hasNormalTexture ? null : 'no normal texture on this material to scale',
          ),
          scalar(
            'Occlusion',
            'occlusionStrength',
            1,
            'Scales baked ambient occlusion',
            'glTF occlusionTexture.strength — how much the baked occlusion darkens crevices.',
            material.hasOcclusionTexture ? null : 'no occlusion texture on this material to weight',
          ),
          color(
            'Tint',
            'baseColor',
            'Multiplies the albedo — white is untinted',
            'glTF baseColorFactor — multiplies the albedo texture per channel. White leaves the texture untouched; alpha is deliberately not editable here.',
          ),
          color(
            'Emissive',
            'emissive',
            'Self-illumination color — black is off',
            'glTF emissiveFactor — self-illumination, texture or not. Black is off; ADR-018 eyes are the first real consumer.',
          ),
        );
      }
      section.append(refusal);
      return { signature: signatureOf(materials), rows };
    };

    const render = (): void => {
      const state = deps.materials();
      if (state.kind === 'refused') {
        section.replaceChildren(refusal);
        built = null;
        refusal.hidden = false;
        refusal.textContent = `No materials to edit: ${state.reason}`;
        return;
      }
      refusal.hidden = true;
      if (built === null || built.signature !== signatureOf(state.materials)) built = buildRows(state.materials);
      for (const row of built.rows) row.render();
    };
    const unsubscribe = store.subscribe(render);
    render();

    return {
      dispose(): void {
        unsubscribe();
        section.remove();
      },
    };
  };
}
